import * as PIXI from "pixi.js";

export interface InputState {
  left: boolean;
  right: boolean;
  up: boolean;
  down: boolean;
  sprint: boolean;
  interact: boolean; // New interaction key
  cancel: boolean; // Cancel/Escape key
  toggleColliders: boolean; // Toggle collider visibility
  toggleInventory: boolean; // Toggle inventory display
  // Smooth directional values for joystick (range: -1 to 1)
  horizontalAxis: number;
  verticalAxis: number;
}

export class InputManager {
  private keys: { [key: string]: boolean } = {};
  private inputState: InputState = {
    left: false,
    right: false,
    up: false,
    down: false,
    sprint: false,
    interact: false,
    cancel: false,
    toggleColliders: false,
    toggleInventory: false,
    horizontalAxis: 0,
    verticalAxis: 0,
  };

  private joystickActive = false;
  private joystickCenter = { x: 0, y: 0 };
  private joystickRadius = 50;
  private lastInteractState = false;
  private lastUpState = false;
  private lastDownState = false;
  private lastCancelState = false;
  private lastToggleCollidersState = false;
  private lastToggleInventoryState = false;
  private mobileControls: HTMLElement | null = null;
  private pixiUICheckCallback: ((x: number, y: number) => boolean) | null = null;
  private isMobileDevice: boolean = false;
  private isTabletDevice: boolean = false;
  private keyboardDisabled: boolean = false;

  private interactionButton!: PIXI.Container;
  private buttonSprite!: PIXI.Sprite;
  private fullScreenButton!: PIXI.Container;


  private static instance: InputManager;

  // Display configuration
  private readonly collapsedWidth = 150
  private readonly collapsedHeight = 150;

  private screenWidth: number;
  private screenHeight: number;

  constructor(screenWidth: number, screenHeight: number) {
    InputManager.instance = this;
    this.screenWidth = screenWidth
    this.screenHeight = screenHeight

    this.checkMobileDevice();
    this.setupKeyboardControls();
    this.setupMobileControls();
    this.setupFullScreenButton()
  }

  private checkMobileDevice(): void {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isIpad = /iPad/i.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    // Simple mobile detection based on UA and touch capability
    if (/android/i.test(userAgent) || /iPhone|iPod/i.test(userAgent) || isIpad) {
      this.isMobileDevice = true;
    } else if (isTouch && window.innerWidth < 1024) {
      // Small touch screens also count as mobile
      this.isMobileDevice = true;
    } else {
      this.isMobileDevice = false;
    }

    const isAndroidTablet = /android/i.test(userAgent) && !/mobile/i.test(userAgent);
    const minDimen = Math.min(window.innerWidth, window.innerHeight);
    const isTabletBySize = this.isMobileDevice && minDimen >= 600;

    this.isTabletDevice = isIpad || isAndroidTablet || isTabletBySize;

    console.log(`InputManager: Device detected as ${this.isMobileDevice ? (this.isTabletDevice ? 'Tablet' : 'Mobile') : 'Desktop'}`);
  }

  public getButton(): PIXI.Container {
    return this.interactionButton;
  }

  public getFullScreenButton(): PIXI.Container {
    return this.fullScreenButton;
  }

  public updateScreenSize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;

    if (this.interactionButton) {
      this.updateButtonPosition();
    }
    
    if (this.fullScreenButton) {
      this.updateFullScreenButtonPosition();
    }
  }

  private updateButtonPosition(): void {
    const referenceWidth = 1280;
    const minScale = 0.7;
    const maxScale = 1.0;
    let scale = this.screenWidth / referenceWidth;
    scale = Math.max(minScale, Math.min(maxScale, scale));

    if (this.interactionButton) {
      this.interactionButton.scale.set(scale);

      const targetScreenSize = 140;
      this.buttonSprite.width = targetScreenSize;
      this.buttonSprite.height = targetScreenSize;

      const rightMargin = 20 * scale;
      const bottomMargin = 20 * scale;

      const screenButtonX = this.screenWidth - (targetScreenSize * scale) - rightMargin;
      const screenButtonY = this.screenHeight - (targetScreenSize * scale) - bottomMargin;

      this.interactionButton.x = screenButtonX;
      this.interactionButton.y = screenButtonY;
    }
  }

  private updateFullScreenButtonPosition(): void {
    if (this.fullScreenButton) {
      const screenButtonX = this.screenWidth - 85;
      const screenButtonY = 70;

      this.fullScreenButton.x = screenButtonX;
      this.fullScreenButton.y = screenButtonY;
    }
  }

  public isMobile(): boolean {
    return this.isMobileDevice;
  }

  public isTablet(): boolean {
    return this.isTabletDevice;
  }

  public static getInstance(): InputManager {
    if (!InputManager.instance) {
      console.warn("InputManager accessed before initialization");
    }
    return InputManager.instance;
  }

  private setupKeyboardControls(): void {
    window.addEventListener('keydown', e => {
      this.keys[e.code.toLowerCase()] = true;
      this.updateInputState();
    });

    window.addEventListener('keyup', e => {
      this.keys[e.code.toLowerCase()] = false;
      this.updateInputState();
    });
  }

  private setupMobileControls(): void {
    const mobileControls = document.getElementById('mobileControls');
    const joystickKnob = document.getElementById('joystickKnob');

    if (!mobileControls || !joystickKnob) return;

    // Store references to mobile controls for visibility control
    this.mobileControls = mobileControls;

    // Joystick Logic (Mobile Only)
    if (this.isMobileDevice) {
      this.setupJoystick(mobileControls, joystickKnob);
    } else {
      mobileControls.style.display = 'none';
    }

    // Interaction Button Logic (Universal)
    this.setupInteractionButton();
  }

  private setupJoystick(mobileControls: HTMLElement, joystickKnob: HTMLElement): void {
    const handleStart = (clientX: number, clientY: number) => {
      this.joystickActive = true;

      // Fixed Joystick: Use the existing element position as center
      const rect = mobileControls.getBoundingClientRect();
      this.joystickCenter = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      };

      mobileControls.classList.add('active');

      this.updateJoystick(clientX, clientY, joystickKnob);
    };

    const handleMove = (clientX: number, clientY: number) => {
      if (this.joystickActive) {
        this.updateJoystick(clientX, clientY, joystickKnob);
      }
    };

    const handleEnd = () => {
      this.joystickActive = false;
      joystickKnob.style.transform = 'translate(0, 0)';
      mobileControls.classList.remove('active');

      this.inputState = {
        ...this.inputState,
        left: false,
        right: false,
        up: false,
        down: false,
        sprint: false,
        horizontalAxis: 0,
        verticalAxis: 0,
      };
      // Remove sprint visual feedback
      if (this.mobileControls) {
        this.mobileControls.classList.remove('sprint');
      }
    };

    // Touch events - listen on document for dynamic joystick
    document.addEventListener('touchstart', e => {
      const touch = e.touches[0];

      // Check if touching a PIXI UI element
      if (this.pixiUICheckCallback?.(touch.clientX, touch.clientY)) return;

      e.preventDefault();
      handleStart(touch.clientX, touch.clientY);
    });

    document.addEventListener('touchmove', e => {
      e.preventDefault();
      const touch = e.touches[0];
      handleMove(touch.clientX, touch.clientY);
    });

    document.addEventListener('touchend', e => {
      e.preventDefault();
      handleEnd();
    });

    // Mouse events for testing on desktop - listen on document
    document.addEventListener('mousedown', e => {
      // Check if clicking a PIXI UI element
      if (this.pixiUICheckCallback?.(e.clientX, e.clientY)) return;

      handleStart(e.clientX, e.clientY);
    });

    document.addEventListener('mousemove', e => {
      handleMove(e.clientX, e.clientY);
    });

    document.addEventListener('mouseup', () => {
      handleEnd();
    });
  }

  private async setupInteractionButton() {
    // Create toggle button (collapsed state)
    this.interactionButton = new PIXI.Container();

    const texture1 = await PIXI.Assets.load('/assets/ui/b_interaction1.png');
    const texture2 = await PIXI.Assets.load('/assets/ui/b_interaction2.png');

    this.buttonSprite = new PIXI.Sprite(PIXI.Texture.EMPTY);
    this.buttonSprite.width = this.collapsedWidth;
    this.buttonSprite.height = this.collapsedHeight;
    this.buttonSprite.anchor.set(0);

    this.interactionButton.addChild(this.buttonSprite);
    this.interactionButton.interactive = true;
    this.interactionButton.cursor = 'pointer';

    this.buttonSprite.texture = texture1

    const triggerInteract = (active: boolean) => {
      this.inputState.interact = active;
    };

    // Interaction logic using loaded textures
    this.interactionButton.on('pointerdown', () => {
      this.buttonSprite.texture = texture2;
      triggerInteract(true)
    });
    this.interactionButton.on('pointerup', () => {
      this.buttonSprite.texture = texture1;
    });
    this.interactionButton.on('pointerout', () => {
      this.buttonSprite.texture = texture1;
    });

    this.interactionButton.addEventListener('touchstart', (e) => {
      e.preventDefault();
      e.stopPropagation(); // Prevent joystick from picking this up
      triggerInteract(true);
    });
    this.interactionButton.addEventListener('touchend', (e) => {
      e.preventDefault();
      triggerInteract(false);
    });
    this.interactionButton.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      triggerInteract(true);
    });
    this.interactionButton.addEventListener('mouseup', (e) => {
      e.preventDefault();
      triggerInteract(false);
    });

    // Apply initial position and scale
    this.updateButtonPosition();
  }

  private async setupFullScreenButton() {
    // Create toggle button (collapsed state)
    this.fullScreenButton = new PIXI.Container();

    const texture1 = await PIXI.Assets.load('/assets/ui/b_interaction1.png');

    let fullScreenSprite = new PIXI.Sprite(texture1);
    fullScreenSprite.width = 80;
    fullScreenSprite.height = 80;
    fullScreenSprite.anchor.set(0);

    this.fullScreenButton.addChild(fullScreenSprite);
    this.fullScreenButton.interactive = true;
    this.fullScreenButton.cursor = 'pointer';

    this.fullScreenButton.on('pointerdown', () => {
      // alert("boi")
      document.documentElement.requestFullscreen().catch((err) => {
        console.log("Fullscreen request failed:", err);
      });
    });

    // Apply initial position
    this.updateFullScreenButtonPosition();
  }

  private updateJoystick(clientX: number, clientY: number, knob: HTMLElement): void {
    const dx = clientX - this.joystickCenter.x;
    const dy = clientY - this.joystickCenter.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    const clampedDistance = Math.min(distance, this.joystickRadius);
    const angle = Math.atan2(dy, dx);

    const knobX = Math.cos(angle) * clampedDistance;
    const knobY = Math.sin(angle) * clampedDistance;

    knob.style.transform = `translate(${knobX}px, ${knobY}px)`;

    // Calculate smooth directional values (-1 to 1)
    this.inputState.horizontalAxis = knobX / this.joystickRadius;
    this.inputState.verticalAxis = knobY / this.joystickRadius;

    // Update discrete input state based on joystick position (for backward compatibility)
    const threshold = 20;
    this.inputState.left = knobX < -threshold;
    this.inputState.right = knobX > threshold;
    this.inputState.up = knobY < -threshold;
    this.inputState.down = knobY > threshold;

    // Sprint activation: when joystick is pushed beyond the circle boundary with margin
    const sprintThreshold = this.joystickRadius * 1.7; // 10% margin beyond the circle
    this.inputState.sprint = distance >= sprintThreshold;

    // Visual feedback for sprint state
    if (this.mobileControls) {
      if (this.inputState.sprint) {
        this.mobileControls.classList.add('sprint');
      } else {
        this.mobileControls.classList.remove('sprint');
      }
    }
  }

  private updateInputState(): void {
    if (this.keyboardDisabled) {
      this.inputState.left = false;
      this.inputState.right = false;
      this.inputState.up = false;
      this.inputState.down = false;
      this.inputState.sprint = false;
      this.inputState.interact = false;
      this.inputState.cancel = false;
      this.inputState.toggleColliders = false;
      this.inputState.toggleInventory = false;
      this.inputState.horizontalAxis = 0;
      this.inputState.verticalAxis = 0;
      return;
    }

    this.inputState.left = this.keys.keya || this.keys.arrowleft || false;
    this.inputState.right = this.keys.keyd || this.keys.arrowright || false;
    this.inputState.up = this.keys.keyw || this.keys.arrowup || false;
    this.inputState.down = this.keys.keys || this.keys.arrowdown || false;
    this.inputState.sprint = this.keys.shiftleft || this.keys.shiftright || false;
    this.inputState.interact = this.keys.keye || this.keys.space || false;
    this.inputState.cancel = this.keys.escape || false;
    this.inputState.toggleColliders = this.keys.keyc || false;
    this.inputState.toggleInventory = this.keys.keyi || false;

    // Update axis values for keyboard input (digital to analog conversion)
    this.inputState.horizontalAxis = 0;
    this.inputState.verticalAxis = 0;

    if (this.inputState.left) this.inputState.horizontalAxis -= 1;
    if (this.inputState.right) this.inputState.horizontalAxis += 1;
    if (this.inputState.up) this.inputState.verticalAxis -= 1;
    if (this.inputState.down) this.inputState.verticalAxis += 1;
  }

  public getInputState(): InputState {
    return { ...this.inputState };
  }

  public wasInteractPressed(): boolean {
    const currentInteractState = this.inputState.interact;
    const wasPressed = currentInteractState && !this.lastInteractState;
    this.lastInteractState = currentInteractState;
    return wasPressed;
  }

  public wasUpPressed(): boolean {
    const currentUpState = this.inputState.up;
    const wasPressed = currentUpState && !this.lastUpState;
    this.lastUpState = currentUpState;
    return wasPressed;
  }

  public wasDownPressed(): boolean {
    const currentDownState = this.inputState.down;
    const wasPressed = currentDownState && !this.lastDownState;
    this.lastDownState = currentDownState;
    return wasPressed;
  }

  public wasCancelPressed(): boolean {
    const currentCancelState = this.inputState.cancel;
    const wasPressed = currentCancelState && !this.lastCancelState;
    this.lastCancelState = currentCancelState;
    return wasPressed;
  }

  public wasToggleCollidersPressed(): boolean {
    const currentToggleCollidersState = this.inputState.toggleColliders;
    const wasPressed = currentToggleCollidersState && !this.lastToggleCollidersState;
    this.lastToggleCollidersState = currentToggleCollidersState;
    return wasPressed;
  }

  public wasToggleInventoryPressed(): boolean {
    const currentToggleInventoryState = this.inputState.toggleInventory;
    const wasPressed = currentToggleInventoryState && !this.lastToggleInventoryState;
    this.lastToggleInventoryState = currentToggleInventoryState;
    return wasPressed;
  }

  public setMobileControlsVisible(visible: boolean): void {
    if (!this.isMobileDevice) return;

    if (this.mobileControls) {
      if (visible) {
        this.mobileControls.classList.remove('hidden');
        this.mobileControls.style.display = 'flex';
      } else {
        this.mobileControls.classList.add('hidden');
        this.mobileControls.style.display = 'none';
      }
    }
  }

  public setInteractButtonVisible(visible: boolean): void {
    if (this.interactionButton) {
      this.interactionButton.visible = visible;
    }
  }

  public async setInteractButtonActive(active: boolean) {
    if (!this.buttonSprite) return;
    
    const texture1 = await PIXI.Assets.load('/assets/ui/b_interaction1.png');
    const texture2 = await PIXI.Assets.load('/assets/ui/b_interaction2.png');

    if (active) {
      this.buttonSprite.texture = texture2;
    } else {
      this.buttonSprite.texture = texture1;
    }
  }

  public setPixiUICheckCallback(callback: (x: number, y: number) => boolean): void {
    this.pixiUICheckCallback = callback;
  }

  public setKeyboardDisabled(disabled: boolean): void {
    this.keyboardDisabled = disabled;
    if (disabled) {
      this.keys = {};
      this.updateInputState();
    }
  }
}
